/**
 * Mapbox component
 */

/** Dependencies -------------------------------------------- */

// External

import { useEffect, useRef, useState } from 'react'
import mapboxGl from 'mapbox-gl/dist/mapbox-gl-csp'
import MapboxWorker from 'worker-loader!mapbox-gl/dist/mapbox-gl-csp-worker'
import { bbox as boundingBox, buffer, polygon, multiPolygon } from 'turf'

import { Map, Source, Layer } from 'react-map-gl'

// Internal

import { mapInitialBounds, mapMaxBounds, mapMaxZoom, getPlugin, getDevelopmentLevel } from '../dictionary'
import { useAppState } from '../state'

import { getCssVar, ucFirst } from '../../helpers'

/** Constants ----------------------------------------------- */

const geoJsonPolygonType = 'Polygon'
const geoJsonMultiPolygonType = 'MultiPolygon'

const stateLabelsLayerId = 'state-labels'
const stateBoundsSourceId = 'state-bounds'
const stateFillsLayerId = 'state-fills'
const stateStrokesLayerId = 'state-strokes'

const mapBoundsBufferKm = 500
const initialAggregateLayerFilter = [ 'has', 'aggregateId' ]
const choroplethTransitionDuration = 250

const initialAggregateStrokeStyle = {
    'line-width'           : [ 'interpolate', [ 'linear' ], [ 'zoom' ], 0, 0.4, 22, 4 ],
    'line-color-transition': { duration: choroplethTransitionDuration }
}

/** Helpers ------------------------------------------------ */

/**
 * Gets an aggregate's feature from a source data.
 */
function getAggregateFeature( source, aggregate ) {
    return source.features.find( ( feature ) => feature.properties.aggregateId === aggregate.id )
}

/**
 * Gets a feature's bounding box.
 * Good for zooming straight to a feature.
 */
function getFeatureBoundingBox( feature, shouldBuffer = false ) {

    const rawPolygonLike = feature.geometry.type === geoJsonMultiPolygonType
        ? multiPolygon( feature.geometry.coordinates )
        : polygon( feature.geometry.coordinates )

    const polishedPolygonLike = shouldBuffer
        ? buffer( rawPolygonLike, mapBoundsBufferKm )
        : rawPolygonLike

    const rawBoundingBox = boundingBox( polishedPolygonLike )
    return [ [ rawBoundingBox[0], rawBoundingBox[1] ], [ rawBoundingBox[2], rawBoundingBox[3] ] ]
}

/**
 * Gets all of the development level colors as data-driven style properties for mapbox paint styles.
 * Used to build the development level choropleth.
 */
function getDevelopmentLevelColorStyleCases( developmentLevel, mapContainer, color = 'color' ) {
    
    color = ucFirst( color )

    const defaultColor = getCssVar( `developmentLevel${developmentLevel.numericValue}${color}Default`, mapContainer )
    const interactionColor = getCssVar( `developmentLevel${developmentLevel.numericValue}${color}Interaction`, mapContainer )
    const activeColor = getCssVar( `developmentLevel${developmentLevel.numericValue}${color}Active`, mapContainer )
    const inactiveColor = getCssVar( `developmentLevel${developmentLevel.numericValue}${color}Inactive`, mapContainer )

    return [
        [ 'all', [ '==', [ 'get', 'aggregateDevelopmentLevelNumericValue' ], developmentLevel.numericValue ], [ '==', [ 'feature-state', 'active' ], true ] ], activeColor,
        [ 'all', [ '==', [ 'get', 'aggregateDevelopmentLevelNumericValue' ], developmentLevel.numericValue ], [ '==', [ 'feature-state', 'hover' ], true ] ], interactionColor,
        [ 'all', [ '==', [ 'get', 'aggregateDevelopmentLevelNumericValue' ], developmentLevel.numericValue ], [ '==', [ 'feature-state', 'inactive' ], true ] ], inactiveColor,
        [ '==', [ 'get', 'aggregateDevelopmentLevelNumericValue' ], developmentLevel.numericValue ], defaultColor,
    ]            
}

/**
 * Fits the map to a given set of boundaries.
 */
function fitMapToBounds( map, bounds, options = {}, eventData = {} ) {
    map.fitBounds( bounds || mapInitialBounds, { padding: parseInt( getCssVar( 'mapboxPadding' ) ), ...options }, eventData )
}

/**
 * Sets feature states for every feature from a given source.
 */
function setSourceFeatureStates( source, sourceId, featureState, map ) {

    const features = source.features

    for ( let feature of features ) {
        map.setFeatureState( { source: sourceId, id: feature.id }, featureState )
    }
}

/** Main ---------------------------------------------------- */

const baseClassName = 'mapbox'

export default function Mapbox() {

    // Constants ------------------------ */

    const plugin = getPlugin()

    const setLoaded = useAppState( ( state ) => state.setLoaded )
    const setError = useAppState( ( state ) => state.setError )
    const stateBoundsData = useAppState( ( state ) => state.stateBoundsData )
    const aggregate = useAppState(  ( state ) => state.aggregate )
    const setAggregate = useAppState(  ( state ) => state.setAggregate )

    const mapContainerRef = useRef( null )

    const setMap = useAppState( ( state ) => state.setMap )
    const latitude = useAppState( ( state ) => state.latitude )
    const longitude = useAppState( ( state ) => state.longitude )
    const zoom = useAppState( ( state ) => state.zoom )
    const setViewState = useAppState( ( state ) => state.setViewState )
    const maxBounds = useAppState( ( state ) => state.maxBounds )
    const setMaxBounds = useAppState( ( state ) => state.setMaxBounds )

    const [ stateFillsPaintStyle, setStateFillsPaintStyle ] = useState( {} )
    const [ stateStrokesPaintStyle, setStateStrokesPaintStyle ] = useState( {} )

    const mapRef = useRef( null );

    // Handlers ------------------------- */   

    /**
     * Necessary setup for and reveal of mapbox integration.
     * Runs when the map receives a load event.
     */
    function handleLoad() {

        const mapLoadTime = Date.now()
        const elapsedTime = mapLoadTime - plugin.initTime

        function resolveLoad() {
            mapRef.current.getMap().touchZoomRotate.disableRotation()
            mapRef.current.moveLayer( stateLabelsLayerId )
            setMap( mapRef.current )
            fitMapToBounds( mapRef.current )
            setLoaded()
        }

        if ( elapsedTime < plugin.minLoadingTime ) {
            window.setTimeout( resolveLoad, plugin.minLoadingTime - elapsedTime )
            return            
        }

        resolveLoad()
    }

    /**
     * Sets the global map view state. Logging camera props here is helpful for debugging, too.
     * Runs whenever the map's camera moves.
     */
    function handleMove( e ) {
        setViewState( e.viewState )
    }

    /**
     * Sets the maximum bounding box of the map after an aggregate is chosen.
     * Keeps a user from scrolling too far away from their intended aggregate.
     */
    function handleMoveEnd( e ) {
        if ( e.isFromAggregateSet ) {
            setMaxBounds( aggregate ? getFeatureBoundingBox( getAggregateFeature( stateBoundsData, aggregate ), true ) : mapMaxBounds )
        }
    }

    /**
     * Necessary setup for and reveal of mapbox integration.
     * Runs when the map receives a load event.
     */
    function handleMouseMove( e ) {

        if ( ! mapRef.current?.isStyleLoaded() ) return

        const features = e.features || null
        setSourceFeatureStates( stateBoundsData, stateBoundsSourceId, { hover: false }, mapRef.current )

        if ( ! features || ! features.length ) {
            mapRef.current.getCanvas().style.cursor = 'auto'
            return
        }
        
        const hoveredState = features[0]
        mapRef.current.getCanvas().style.cursor = 'pointer'

        mapRef.current.setFeatureState(
            { source: stateBoundsSourceId, id: hoveredState.id },
            { hover: true }
        )
    }

    /**
     * Discerns the severity of a map error and initializes the app error state if it's severe enough.
     */
    function handleError( e ) {
        // console.log( e )
        // setError()
    }

    /**
     * Sets the global aggregate state if a state boundary is clicked on.
     * Runs when the map is clicked on.
     */
    function handleClick( e ) {

        const features = e.features || null
        if ( ! features || ! features.length ) return

        const clickedAggregateId = features[0].properties.aggregateId
        if ( clickedAggregateId === aggregate?.id ) return

        setAggregate( features[0].properties.aggregateId )        
    }

    // Effects -------------------------- */

    /**
     * Necessary setup for mapbox integration.
     * Determines compatibility of and sets up styles for the map.
     * Runs once when the map component mounts, but before the map fully loads.
     */
    function mountEffect() {

        if ( ! mapboxGl.supported() ) {
            setError()
            return
        }

        setStateFillsPaintStyle( {
            'fill-color-transition': { duration: choroplethTransitionDuration },
            'fill-color': [
                'case',
                ...getDevelopmentLevelColorStyleCases( getDevelopmentLevel( 'developmentLevel0' ), mapContainerRef.current ),
                ...getDevelopmentLevelColorStyleCases( getDevelopmentLevel( 'developmentLevel1' ), mapContainerRef.current ),
                ...getDevelopmentLevelColorStyleCases( getDevelopmentLevel( 'developmentLevel2' ), mapContainerRef.current ),
                ...getDevelopmentLevelColorStyleCases( getDevelopmentLevel( 'developmentLevel3' ), mapContainerRef.current ),
                'transparent'
            ]
        } )

        setStateStrokesPaintStyle( {
            ...initialAggregateStrokeStyle,
            'line-color': [
                'case',
                ...getDevelopmentLevelColorStyleCases( getDevelopmentLevel( 'developmentLevel0' ), mapContainerRef.current, 'outlineColor' ),
                ...getDevelopmentLevelColorStyleCases( getDevelopmentLevel( 'developmentLevel1' ), mapContainerRef.current, 'outlineColor' ),
                ...getDevelopmentLevelColorStyleCases( getDevelopmentLevel( 'developmentLevel2' ), mapContainerRef.current, 'outlineColor' ),
                ...getDevelopmentLevelColorStyleCases( getDevelopmentLevel( 'developmentLevel3' ), mapContainerRef.current, 'outlineColor' ),
                getCssVar( 'outlineColorMinus1', mapContainerRef.current )
            ],
        } )
    }

    /**
     * Sets feature states that power the development level choropleth.
     * Runs whenever the global aggregate state changes, assuming the state bounds data is available.
     */
    function aggregateEffect() {

        if ( ! stateBoundsData || ! mapRef.current?.isStyleLoaded() ) return

        setMaxBounds( mapMaxBounds )

        if ( ! aggregate ) {
            setSourceFeatureStates( stateBoundsData, stateBoundsSourceId, { active: false, inactive: false }, mapRef.current )
            fitMapToBounds( mapRef.current, null, {}, { isFromAggregateSet: true } )
            return
        }

        const aggregateFeature = getAggregateFeature( stateBoundsData, aggregate )
        const otherFeatures = stateBoundsData.features.filter( ( feature ) => feature.properties.aggregateId !== aggregate.id )

        mapRef.current.setFeatureState(
            { source: stateBoundsSourceId, id: aggregateFeature.id },
            { active: true, inactive: false }
        )

        for ( let otherFeature of otherFeatures ) {
            mapRef.current.setFeatureState(
                { source: stateBoundsSourceId, id: otherFeature.id },
                { active: false, inactive: true }
            )
        }

        const aggregateBoundingBox = getFeatureBoundingBox( aggregateFeature )
        fitMapToBounds( mapRef.current, aggregateBoundingBox, {}, { isFromAggregateSet: true } )
    }

    useEffect( mountEffect, [] )
    useEffect( aggregateEffect, [ aggregate, stateBoundsData ] )

    // Render -------------------------- */

    return (
        <div className={ baseClassName } ref={ mapContainerRef }>
            <Map
                attributionControl ={ false }
                // boxZoom            ={ false }
                className          ={ baseClassName }
                dragRotate         ={ false }
                interactiveLayerIds={ [ stateFillsLayerId ] }
                latitude           ={ latitude }
                longitude          ={ longitude }
                logoPosition       ='top-right'
                mapboxAccessToken  ={ plugin.mapboxAccessToken }
                mapLib             ={ mapboxGl }
                mapStyle           ={ plugin.mapboxStyleUrl }
                maxBounds          ={ maxBounds }
                maxZoom            ={ mapMaxZoom }
                ref                ={ mapRef }
                touchPitch         ={ false }
                workerClass        ={ MapboxWorker }
                zoom               ={ zoom }
                onClick            ={ handleClick }
                onError            ={ handleError }
                onLoad             ={ handleLoad }
                onMove             ={ handleMove }
                onMoveEnd          ={ handleMoveEnd }
                onMouseMove        ={ handleMouseMove }
            >
                <Source id={ stateBoundsSourceId } type="geojson" data={ stateBoundsData }>
                    <Layer id={ stateStrokesLayerId  } type="line" paint ={ stateStrokesPaintStyle }/>
                    <Layer id={ stateFillsLayerId } type="fill" filter={ initialAggregateLayerFilter } paint={ stateFillsPaintStyle }/>
                </Source>
            </Map>  
        </div>
    )
}
